import { Component, OnInit } from '@angular/core';
import { BaseStructureComponent } from '../base-structure/base-structure.component';
import {
  getValue,
  isDefined,
  isDefinedAndFilledArray,
  isDefinedAndFilledString,
} from '../../../../shared/utility/verify.lib';
import { IExportStructure } from '../../../../models/export/export-structure.model';
import { combineLatest, filter, first, takeUntil } from 'rxjs';
import { IExportConfiguration } from '../../../../models/export/export-configuration.model';
import { updateSavedConfiguration } from '../../../../redux/mydoc.action';
import { cloneDeep, isEqual } from 'lodash';
import { MatOptionSelectionChange } from '@angular/material/core';
import { ExportStructureHelperService } from '../../services/export-structure-helper/export-structure-helper.service';
import { Store } from '@ngrx/store';
import { TrackingHelperService } from '../../services/tracking-helper/tracking-helper.service';

@Component({
  selector: 'app-file-structure',
  templateUrl: './file-structure.component.html',
  styleUrls: [
    '../base-structure/base-structure.component.scss',
    './file-structure.component.scss',
  ],
})
export class FileStructureComponent
  extends BaseStructureComponent
  implements OnInit
{
  public fileExampleText: string = null;
  public fileNameEnding: string = '.pdf';

  public constructor(
    protected override store: Store,
    protected override trackingHelperService: TrackingHelperService,
    private exportStructureHelper: ExportStructureHelperService
  ) {
    super(store, trackingHelperService);
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    this.separator = '_';
    this.fileExampleText = this.fileNameEnding;
    this.subscribeToSavedConfig();

    combineLatest([this.defaultConfiguration$, this.savedConfiguration$])
      .pipe(takeUntil(this.completeAll$))
      .subscribe(
        ([defaultConfig, savedConfig]: [
          IExportConfiguration,
          IExportConfiguration
        ]) => {
          this.didDropdownDefaultChange =
            this.exportStructureHelper.didShortNamesChange(
              defaultConfig,
              savedConfig,
              this.getLatestValueFromSubscription(this.documentSession$),
              true,
              false
            );
        }
      );
  }

  public onSetFileSetting(
    event: MatOptionSelectionChange,
    setting: IExportStructure,
    index: number
  ): void {
    if (getValue(() => event.isUserInput)) {
      this.preselectedInDropdown[index] = getValue(() => setting.shortName);
      const oldSavedConfig: IExportConfiguration =
        this.getLatestValueFromSubscription(this.savedConfiguration$);
      const newFileSettings = cloneDeep(oldSavedConfig.fileSetting);
      if (index < getValue(() => oldSavedConfig.fileSetting.length, 0)) {
        newFileSettings[index] = { ...setting, level: index + 1 };
      }

      this.store.dispatch(
        updateSavedConfiguration({
          configuration: { ...oldSavedConfig, fileSetting: newFileSettings },
        })
      );
    }
  }

  public onClearDropdown(index: number): void {
    const oldSavedConfig: IExportConfiguration =
      this.getLatestValueFromSubscription(this.savedConfiguration$);
    const newfileSettings = cloneDeep(oldSavedConfig.fileSetting);
    if (index < getValue(() => oldSavedConfig.fileSetting.length, 0)) {
      newfileSettings[index] = null;
    }
    this.preselectedInDropdown[index] = null;

    this.store.dispatch(
      updateSavedConfiguration({
        configuration: { ...oldSavedConfig, fileSetting: newfileSettings },
      })
    );
  }

  public resetFileSettings(): void {
    const oldSavedConfig: IExportConfiguration =
      this.getLatestValueFromSubscription(this.savedConfiguration$);
    const defaultConfig: IExportConfiguration =
      this.getLatestValueFromSubscription(this.defaultConfiguration$);
    const defaultFileSettings: IExportStructure[] = getValue(
      () => defaultConfig.fileSetting,
      []
    );
    this.trackingHelperService.trackResetFileFolder(true, this.sessionType);
    this.loadFileDefaultConfiguration();

    this.store.dispatch(
      updateSavedConfiguration({
        configuration: { ...oldSavedConfig, fileSetting: defaultFileSettings },
      })
    );
  }

  protected override initStructure(): void {
    if (isDefinedAndFilledArray(() => this.fileSettings)) {
      this.fileSettings.forEach((structure: IExportStructure) => {
        const shortName: string = this.exportStructureHelper.getMappedShortName(
          structure,
          this.getLatestValueFromSubscription(this.documentSession$)
        );
        const text: string = this.getTextFromStructure(structure);
        if (
          isDefinedAndFilledString(() => shortName) &&
          isDefined(() => text)
        ) {
          this.settingsPartTexts[shortName] = text;
        }
      });
      this.loadFileDefaultConfiguration();
    }
  }

  private loadFileDefaultConfiguration(): void {
    this.defaultConfiguration$
      .pipe(
        filter((defaultConfig: IExportConfiguration) =>
          isDefined(() => defaultConfig)
        ),
        first()
      )
      .subscribe((defaultConfig: IExportConfiguration) => {
        if (isDefinedAndFilledArray(() => defaultConfig.fileSetting)) {
          defaultConfig.fileSetting.forEach(
            (settingStructure: IExportStructure, index: number) => {
              if (
                index < getValue(() => this.preselectedInDropdown.length, 0)
              ) {
                this.preselectedInDropdown[index] =
                  this.exportStructureHelper.getMappedShortName(
                    settingStructure,
                    this.getLatestValueFromSubscription(this.documentSession$)
                  );
              }
            }
          );
        }
      });
  }

  private subscribeToSavedConfig(): void {
    this.savedConfiguration$
      .pipe(takeUntil(this.completeAll$))
      .subscribe((savedConfig: IExportConfiguration) => {
        if (isDefinedAndFilledArray(() => savedConfig.fileSetting)) {
          const savedSettings: IExportStructure[] = getValue(
            () => savedConfig.fileSetting,
            []
          );
          const filteredSettings: IExportStructure[] =
            this.exportStructureHelper.filterAvailableStructure(
              savedSettings,
              this.exportStructureHelper.getShortNames(
                this.fileSettings,
                this.documentSession
              ),
              this.documentSession
            );

          this.alreadyIncludedShortNames =
            this.exportStructureHelper.getShortNames(
              getValue(() => filteredSettings),
              this.getLatestValueFromSubscription(this.documentSession$)
            );

          this.fileExampleText = this.updateExampleText(filteredSettings);
          if (!isEqual(filteredSettings, savedSettings)) {
            this.preselectedInDropdown =
              this.exportStructureHelper.updatePreselectedInDropdown(
                filteredSettings,
                this.documentSession,
                this.preselectedInDropdown
              );
            this.store.dispatch(
              updateSavedConfiguration({
                configuration: {
                  ...savedConfig,
                  fileSetting: filteredSettings,
                },
              })
            );
          }
        }
      });
  }
}
